import React, { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { Plus, Trash2, MapPin, User as UserIcon, Search, History } from "lucide-react";
import api, { formatApiError } from "../lib/api";
import PageHeader from "../components/PageHeader";
import Modal from "../components/Modal";
import AdminDeleteModal from "../components/AdminDeleteModal";
import { useAuth } from "../contexts/AuthContext";
import { fmtDate } from "../lib/format";

const EMPTY_ADDRESS = {
    label: "",
    address1: "",
    address2: "",
    city: "",
    state: "",
    postal_code: "",
    is_primary: false,
};

const EMPTY_CONTACT = {
    full_name: "",
    email: "",
    direct_phone: "",
    is_primary: false,
    active: true,
};

const newUid = () =>
    (typeof crypto !== "undefined" && crypto.randomUUID)
        ? crypto.randomUUID()
        : `c-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;

const withUid = (obj) => ({ _uid: newUid(), ...obj });

function CustomerForm({ initial, onSubmit, onCancel }) {
    const [name, setName] = useState(initial?.name || "");
    const [phone, setPhone] = useState(initial?.company_phone || "");
    const [notes, setNotes] = useState(initial?.notes || "");
    const [active, setActive] = useState(initial?.active ?? true);
    const [addresses, setAddresses] = useState(
        initial?.addresses?.length
            ? initial.addresses.map((a) => ({ _uid: newUid(), ...a }))
            : [withUid({ ...EMPTY_ADDRESS, is_primary: true })],
    );
    const [contacts, setContacts] = useState(
        (initial?.contacts || []).map((c) => ({ _uid: newUid(), ...c })),
    );
    const [error, setError] = useState("");
    const [submitting, setSubmitting] = useState(false);

    const updateAddr = (idx, patch) =>
        setAddresses((a) => a.map((row, i) => (i === idx ? { ...row, ...patch } : row)));
    const updateContact = (idx, patch) =>
        setContacts((c) => c.map((row, i) => (i === idx ? { ...row, ...patch } : row)));

    const submit = async (e) => {
        e.preventDefault();
        setError("");
        if (!name.trim()) return setError("Customer name is required.");
        setSubmitting(true);
        try {
            // Strip the client-only `_uid` we use as a stable React key.
            const stripUid = (rows) =>
                rows.map(({ _uid, ...rest }) => rest);
            await onSubmit({
                name: name.trim(),
                company_phone: phone,
                notes,
                active,
                addresses: stripUid(addresses),
                contacts: stripUid(contacts).filter((c) => c.full_name?.trim()),
            });
        } catch (err) {
            setError(formatApiError(err?.response?.data?.detail) || err.message);
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <form onSubmit={submit} className="space-y-5" data-testid="customer-form">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                    <label className="block text-xs font-semibold uppercase tracking-wider text-gray-600 mb-1">
                        Customer Name
                    </label>
                    <input
                        className="odr-input w-full rounded-sm border border-gray-300 px-3 text-sm focus:outline-none focus:ring-2 focus:ring-red-500/20 focus:border-red-500"
                        value={name}
                        onChange={(e) => setName(e.target.value)}
                        data-testid="customer-form-name"
                        required
                    />
                </div>
                <div>
                    <label className="block text-xs font-semibold uppercase tracking-wider text-gray-600 mb-1">
                        Company Phone
                    </label>
                    <input
                        className="odr-input w-full rounded-sm border border-gray-300 px-3 text-sm focus:outline-none focus:ring-2 focus:ring-red-500/20 focus:border-red-500"
                        value={phone}
                        onChange={(e) => setPhone(e.target.value)}
                        data-testid="customer-form-phone"
                    />
                </div>
            </div>
            <div>
                <label className="block text-xs font-semibold uppercase tracking-wider text-gray-600 mb-1">
                    Notes
                </label>
                <textarea
                    rows={2}
                    className="w-full rounded-sm border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-red-500/20 focus:border-red-500"
                    value={notes}
                    onChange={(e) => setNotes(e.target.value)}
                    data-testid="customer-form-notes"
                />
            </div>
            <div className="flex items-center gap-2">
                <input
                    id="active-toggle"
                    type="checkbox"
                    checked={active}
                    onChange={(e) => setActive(e.target.checked)}
                    data-testid="customer-form-active"
                />
                <label htmlFor="active-toggle" className="text-sm text-gray-700">
                    Active
                </label>
            </div>

            {/* Addresses */}
            <div>
                <div className="flex items-center justify-between mb-2">
                    <h4 className="text-xs font-semibold uppercase tracking-wider text-gray-600 flex items-center gap-2">
                        <MapPin className="w-4 h-4" /> Addresses
                    </h4>
                    <button
                        type="button"
                        onClick={() =>
                            setAddresses((a) => [...a, withUid({ ...EMPTY_ADDRESS })])
                        }
                        className="text-sm text-red-700 font-semibold hover:text-red-900 inline-flex items-center gap-1"
                        data-testid="add-address"
                    >
                        <Plus className="w-4 h-4" /> Add address
                    </button>
                </div>
                <div className="space-y-3">
                    {addresses.map((a, idx) => (
                        <div
                            key={a._uid || idx}
                            className="border border-gray-200 rounded-sm p-3 bg-gray-50/50 space-y-2"
                            data-testid={`address-row-${idx}`}
                        >
                            <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
                                <input
                                    placeholder="Label (e.g. Main, Ship-to)"
                                    className="odr-input rounded-sm border border-gray-300 px-3 text-sm bg-white"
                                    value={a.label}
                                    onChange={(e) => updateAddr(idx, { label: e.target.value })}
                                />
                                <input
                                    placeholder="Address line 1"
                                    className="odr-input sm:col-span-2 rounded-sm border border-gray-300 px-3 text-sm bg-white"
                                    value={a.address1}
                                    onChange={(e) => updateAddr(idx, { address1: e.target.value })}
                                />
                            </div>
                            <input
                                placeholder="Address line 2"
                                className="odr-input w-full rounded-sm border border-gray-300 px-3 text-sm bg-white"
                                value={a.address2}
                                onChange={(e) => updateAddr(idx, { address2: e.target.value })}
                            />
                            <div className="grid grid-cols-1 sm:grid-cols-4 gap-2">
                                <input
                                    placeholder="City"
                                    className="odr-input rounded-sm border border-gray-300 px-3 text-sm bg-white"
                                    value={a.city}
                                    onChange={(e) => updateAddr(idx, { city: e.target.value })}
                                />
                                <input
                                    placeholder="State"
                                    className="odr-input rounded-sm border border-gray-300 px-3 text-sm bg-white uppercase"
                                    maxLength={3}
                                    value={a.state}
                                    onChange={(e) =>
                                        updateAddr(idx, { state: e.target.value.toUpperCase() })
                                    }
                                />
                                <input
                                    placeholder="Postal Code"
                                    className="odr-input rounded-sm border border-gray-300 px-3 text-sm bg-white"
                                    value={a.postal_code}
                                    onChange={(e) =>
                                        updateAddr(idx, { postal_code: e.target.value })
                                    }
                                    data-testid={`address-postal-${idx}`}
                                />
                                <div className="flex items-center gap-3 px-1">
                                    <label className="flex items-center gap-1 text-xs text-gray-700">
                                        <input
                                            type="radio"
                                            name="primary-address"
                                            checked={!!a.is_primary}
                                            onChange={() =>
                                                setAddresses((all) =>
                                                    all.map((row, i) => ({
                                                        ...row,
                                                        is_primary: i === idx,
                                                    })),
                                                )
                                            }
                                        />
                                        Primary
                                    </label>
                                    <button
                                        type="button"
                                        onClick={() =>
                                            setAddresses((all) => all.filter((_, i) => i !== idx))
                                        }
                                        className="text-gray-500 hover:text-red-700 ml-auto"
                                        title="Remove"
                                    >
                                        <Trash2 className="w-4 h-4" />
                                    </button>
                                </div>
                            </div>
                        </div>
                    ))}
                </div>
            </div>

            {/* Contacts */}
            <div>
                <div className="flex items-center justify-between mb-2">
                    <h4 className="text-xs font-semibold uppercase tracking-wider text-gray-600 flex items-center gap-2">
                        <UserIcon className="w-4 h-4" /> Contacts
                    </h4>
                    <button
                        type="button"
                        onClick={() => setContacts((c) => [...c, withUid({ ...EMPTY_CONTACT })])}
                        className="text-sm text-red-700 font-semibold hover:text-red-900 inline-flex items-center gap-1"
                        data-testid="add-contact"
                    >
                        <Plus className="w-4 h-4" /> Add contact
                    </button>
                </div>
                {contacts.length === 0 && (
                    <p className="text-xs text-gray-500 italic">
                        No contacts yet. Click &ldquo;Add contact&rdquo; to add one.
                    </p>
                )}
                <div className="space-y-2">
                    {contacts.map((c, idx) => (
                        <div
                            key={c._uid || idx}
                            className="border border-gray-200 rounded-sm p-3 bg-gray-50/50 grid grid-cols-1 sm:grid-cols-4 gap-2 items-center"
                            data-testid={`contact-row-${idx}`}
                        >
                            <input
                                placeholder="Full name"
                                className="odr-input rounded-sm border border-gray-300 px-3 text-sm bg-white"
                                value={c.full_name}
                                onChange={(e) =>
                                    updateContact(idx, { full_name: e.target.value })
                                }
                            />
                            <input
                                placeholder="Email"
                                type="email"
                                className="odr-input rounded-sm border border-gray-300 px-3 text-sm bg-white"
                                value={c.email}
                                onChange={(e) => updateContact(idx, { email: e.target.value })}
                            />
                            <input
                                placeholder="Direct phone"
                                className="odr-input rounded-sm border border-gray-300 px-3 text-sm bg-white"
                                value={c.direct_phone}
                                onChange={(e) =>
                                    updateContact(idx, { direct_phone: e.target.value })
                                }
                            />
                            <div className="flex items-center gap-3">
                                <label className="flex items-center gap-1 text-xs text-gray-700">
                                    <input
                                        type="radio"
                                        name="primary-contact"
                                        checked={!!c.is_primary}
                                        onChange={() =>
                                            setContacts((all) =>
                                                all.map((row, i) => ({
                                                    ...row,
                                                    is_primary: i === idx,
                                                })),
                                            )
                                        }
                                    />
                                    Primary
                                </label>
                                <button
                                    type="button"
                                    onClick={() =>
                                        setContacts((all) => all.filter((_, i) => i !== idx))
                                    }
                                    className="text-gray-500 hover:text-red-700 ml-auto"
                                    title="Remove"
                                >
                                    <Trash2 className="w-4 h-4" />
                                </button>
                            </div>
                        </div>
                    ))}
                </div>
            </div>

            {error && (
                <div className="text-sm border border-red-200 bg-red-50 text-red-700 rounded-sm px-3 py-2">
                    {error}
                </div>
            )}

            <div className="flex justify-end gap-2">
                <button
                    type="button"
                    onClick={onCancel}
                    className="odr-btn rounded-sm border border-gray-300 px-4 text-sm text-gray-700 hover:bg-gray-50"
                    data-testid="customer-form-cancel"
                >
                    Cancel
                </button>
                <button
                    type="submit"
                    disabled={submitting}
                    className="odr-btn rounded-sm bg-[var(--odr-red)] hover:bg-[var(--odr-red-hover)] text-white text-sm font-semibold px-4 disabled:opacity-60"
                    data-testid="customer-form-submit"
                >
                    {submitting ? "Saving…" : initial ? "Save Changes" : "Create Customer"}
                </button>
            </div>
        </form>
    );
}

export default function Customers() {
    const { user } = useAuth();
    const isAdmin = user?.role === "admin";
    const [customers, setCustomers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState("");
    const [search, setSearch] = useState("");
    const [activeOnly, setActiveOnly] = useState(false);
    const [showCreate, setShowCreate] = useState(false);
    const [editing, setEditing] = useState(null);
    const [deleting, setDeleting] = useState(null);
    const [historyOf, setHistoryOf] = useState(null);

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const params = {};
            if (search.trim()) params.q = search.trim();
            if (activeOnly) params.active = true;
            const { data } = await api.get("/customers", { params });
            setCustomers(data);
            setError("");
        } catch (err) {
            setError(formatApiError(err?.response?.data?.detail) || err.message);
        } finally {
            setLoading(false);
        }
    }, [search, activeOnly]);

    useEffect(() => {
        const t = setTimeout(load, 200);
        return () => clearTimeout(t);
    }, [load]);

    return (
        <div data-testid="customers-page">
            <PageHeader
                title="Customers"
                subtitle="Master customer list — multiple addresses, unlimited contacts"
                actions={
                    <button
                        onClick={() => setShowCreate(true)}
                        className="odr-btn inline-flex items-center gap-2 rounded-sm bg-[var(--odr-red)] hover:bg-[var(--odr-red-hover)] text-white text-sm font-semibold px-4"
                        data-testid="customers-add-button"
                    >
                        <Plus className="w-4 h-4" /> New Customer
                    </button>
                }
            />

            <div className="bg-white border border-gray-200 rounded-sm p-3 mb-4 flex flex-wrap gap-3 items-end">
                <div className="relative flex-1 min-w-[240px]">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                    <input
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                        placeholder="Search by name…"
                        className="odr-input w-full rounded-sm border border-gray-300 pl-9 pr-3 text-sm focus:outline-none focus:ring-2 focus:ring-red-500/20 focus:border-red-500"
                        data-testid="customers-search"
                    />
                </div>
                <label className="flex items-center gap-2 text-sm text-gray-700">
                    <input
                        type="checkbox"
                        checked={activeOnly}
                        onChange={(e) => setActiveOnly(e.target.checked)}
                        data-testid="customers-active-only"
                    />
                    Active only
                </label>
            </div>

            {error && (
                <div className="text-sm border border-red-200 bg-red-50 text-red-700 rounded-sm px-3 py-2 mb-4">
                    {error}
                </div>
            )}

            <div className="bg-white border border-gray-200 rounded-sm overflow-x-auto">
                <table className="odr-table" data-testid="customers-table">
                    <thead>
                        <tr>
                            <th>Name</th>
                            <th>Phone</th>
                            <th>City / State</th>
                            <th>Contacts</th>
                            <th>Status</th>
                            <th>Updated</th>
                            <th className="text-right">Actions</th>
                        </tr>
                    </thead>
                    <tbody>
                        {loading && (
                            <tr>
                                <td colSpan={7} className="text-center text-gray-500">
                                    Loading…
                                </td>
                            </tr>
                        )}
                        {!loading && customers.length === 0 && (
                            <tr>
                                <td colSpan={7} className="text-center text-gray-500">
                                    No customers found.
                                </td>
                            </tr>
                        )}
                        {customers.map((c) => {
                            const primary =
                                c.addresses?.find((a) => a.is_primary) ||
                                c.addresses?.[0];
                            return (
                                <tr key={c.id} data-testid={`customer-row-${c.id}`}>
                                    <td className="font-medium">{c.name}</td>
                                    <td className="odr-mono text-xs">{c.company_phone || "—"}</td>
                                    <td className="text-sm text-gray-700">
                                        {primary
                                            ? `${primary.city || ""}${primary.state ? ", " + primary.state : ""}`
                                            : "—"}
                                    </td>
                                    <td className="text-sm">{c.contacts?.length || 0}</td>
                                    <td>
                                        {c.active ? (
                                            <span className="odr-badge odr-badge-open">Active</span>
                                        ) : (
                                            <span className="odr-badge odr-badge-neutral">Inactive</span>
                                        )}
                                    </td>
                                    <td className="text-xs text-gray-500 whitespace-nowrap">
                                        {fmtDate(c.updated_at)}
                                    </td>
                                    <td className="text-right">
                                        <button
                                            onClick={() => setHistoryOf(c)}
                                            className="text-sm text-red-700 hover:text-red-900 font-semibold inline-flex items-center gap-1 mr-3"
                                            data-testid={`history-customer-${c.id}`}
                                            title="Order history"
                                        >
                                            <History className="w-3.5 h-3.5" /> Orders
                                        </button>
                                        <button
                                            onClick={() => setEditing(c)}
                                            className="text-sm text-red-700 hover:text-red-900 font-semibold"
                                            data-testid={`edit-customer-${c.id}`}
                                        >
                                            Edit
                                        </button>
                                        {isAdmin && (
                                            <button
                                                onClick={() => setDeleting(c)}
                                                className="ml-3 text-sm text-red-700 hover:text-red-900 font-semibold inline-flex items-center gap-1"
                                                data-testid={`delete-customer-${c.id}`}
                                                title="Hard-delete (admin)"
                                            >
                                                <Trash2 className="w-3.5 h-3.5" /> Delete
                                            </button>
                                        )}
                                    </td>
                                </tr>
                            );
                        })}
                    </tbody>
                </table>
            </div>

            {showCreate && (
                <Modal title="New Customer" size="lg" onClose={() => setShowCreate(false)}>
                    <CustomerForm
                        onCancel={() => setShowCreate(false)}
                        onSubmit={async (payload) => {
                            await api.post("/customers", payload);
                            setShowCreate(false);
                            await load();
                        }}
                    />
                </Modal>
            )}

            {editing && (
                <Modal
                    title={`Edit ${editing.name}`}
                    size="lg"
                    onClose={() => setEditing(null)}
                >
                    <CustomerForm
                        initial={editing}
                        onCancel={() => setEditing(null)}
                        onSubmit={async (payload) => {
                            await api.patch(`/customers/${editing.id}`, payload);
                            setEditing(null);
                            await load();
                        }}
                    />
                </Modal>
            )}

            <AdminDeleteModal
                open={!!deleting}
                module="customers"
                recordId={deleting?.id}
                endpoint={deleting ? `/customers/${deleting.id}` : ""}
                label={deleting ? `customer '${deleting.name}'` : ""}
                onClose={() => setDeleting(null)}
                onDeleted={() => load()}
                testIdPrefix="delete-customer"
            />

            {historyOf && (
                <CustomerOrderHistoryModal
                    customer={historyOf}
                    onClose={() => setHistoryOf(null)}
                />
            )}
        </div>
    );
}

function CustomerOrderHistoryModal({ customer, onClose }) {
    const [orders, setOrders] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState("");
    const [includeCancelled, setIncludeCancelled] = useState(true);

    useEffect(() => {
        let alive = true;
        (async () => {
            setLoading(true);
            try {
                const { data } = await api.get("/orders", {
                    params: {
                        customer_id: customer.id,
                        include_cancelled: includeCancelled,
                    },
                });
                if (alive) setOrders(data);
            } catch (err) {
                if (alive) setError(formatApiError(err?.response?.data?.detail) || err.message);
            } finally {
                if (alive) setLoading(false);
            }
        })();
        return () => { alive = false; };
    }, [customer.id, includeCancelled]);

    return (
        <Modal
            title={`Orders — ${customer.name}`}
            size="lg"
            onClose={onClose}
        >
            <div data-testid="customer-order-history">
                <div className="flex items-center justify-between text-xs text-gray-600 mb-2">
                    <span>
                        {loading ? "Loading…" : `${orders.length} order${orders.length === 1 ? "" : "s"}`}
                    </span>
                    <label className="inline-flex items-center gap-1">
                        <input
                            type="checkbox"
                            checked={includeCancelled}
                            onChange={(e) => setIncludeCancelled(e.target.checked)}
                            data-testid="history-include-cancelled"
                        />
                        Include cancelled
                    </label>
                </div>
                {error && (
                    <div className="text-sm text-red-700 bg-red-50 border border-red-200 rounded px-2 py-1 mb-2">
                        {error}
                    </div>
                )}
                {!loading && orders.length === 0 && (
                    <div className="text-sm text-gray-500 py-6 text-center border border-dashed rounded">
                        No orders yet for this customer.
                    </div>
                )}
                {orders.length > 0 && (
                    <div className="border rounded overflow-hidden">
                        <table className="odr-table w-full text-sm">
                            <thead>
                                <tr>
                                    <th className="text-left">Internal #</th>
                                    <th className="text-left">Customer PO</th>
                                    <th className="text-left">Status</th>
                                    <th className="text-left">Received</th>
                                    <th className="text-left">Due</th>
                                    <th />
                                </tr>
                            </thead>
                            <tbody>
                                {orders.map((o) => (
                                    <tr
                                        key={o.id}
                                        data-testid={`history-order-row-${o.id}`}
                                    >
                                        <td className="odr-mono text-xs">
                                            <Link
                                                to={`/orders/${o.id}`}
                                                onClick={onClose}
                                                className="text-red-700 hover:text-red-900 font-semibold"
                                                data-testid={`history-order-link-${o.id}`}
                                            >
                                                {o.internal_number || "—"}
                                            </Link>
                                        </td>
                                        <td className="odr-mono text-xs">{o.customer_po || "—"}</td>
                                        <td>
                                            <span className={
                                                "odr-badge " + (
                                                    o.cancelled ? "odr-badge-neutral" :
                                                    o.status === "completed" ? "odr-badge-done" :
                                                    o.status === "open" ? "odr-badge-open" :
                                                    "odr-badge-neutral"
                                                )
                                            }>
                                                {o.cancelled ? "cancelled" : (o.status || "—")}
                                            </span>
                                        </td>
                                        <td className="text-xs text-gray-700">{fmtDate(o.po_date)}</td>
                                        <td className="text-xs text-gray-700">{fmtDate(o.eta)}</td>
                                        <td className="text-right">
                                            <Link
                                                to={`/orders/${o.id}`}
                                                onClick={onClose}
                                                className="text-xs text-red-700 hover:text-red-900 font-semibold"
                                            >
                                                View →
                                            </Link>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>
        </Modal>
    );
}
